import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Grid,
  Button,
  IconButton,
  Tooltip,
  Chip,
  Stack,
  Card,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  CircularProgress,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
  LinearProgress,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Add as AddIcon,
  Campaign as CampaignIcon,
  Email as EmailIcon,
  Sms as SmsIcon,
  Share as SocialIcon,
  Mail as DirectMailIcon,
  Analytics as AnalyticsIcon,
  People as PeopleIcon,
  Schedule as ScheduleIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Stop as StopIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as DuplicateIcon,
  Visibility as ViewIcon,
  TrendingUp as TrendingIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import CampaignList from './components/CampaignList';
import CampaignDetail from './components/CampaignDetail';
import CampaignForm from './components/CampaignForm';
import CampaignAnalytics from './components/CampaignAnalytics';
import CampaignTemplates from './components/CampaignTemplates';

function CampaignsPage() {
  const theme = useTheme();
  const [tab, setTab] = useState(0);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalCampaigns: 0,
    activeCampaigns: 0,
    draftCampaigns: 0,
    scheduledCampaigns: 0,
    completedCampaigns: 0,
    totalSent: 0,
    totalOpened: 0,
    totalClicked: 0,
    averageOpenRate: 0,
    averageClickRate: 0,
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState('create');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    console.log('CampaignsPage mounted');
    loadCampaigns();
    loadStats();
  }, []);

  const loadCampaigns = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/v1/campaigns', {
        credentials: 'include',
        headers: { Authorization: `Bearer ${localStorage.getItem('tkn')}` },
      });

      if (response.ok) {
        const data = await response.json();
        setCampaigns(data.campaigns || []);
      }
    } catch (error) {
      console.error('Error loading campaigns:', error);
      setSnackbar({ open: true, message: 'Failed to load campaigns', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await fetch('/api/v1/campaigns/stats', {
        credentials: 'include',
        headers: { Authorization: `Bearer ${localStorage.getItem('tkn')}` },
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data.stats || {});
      }
    } catch (error) {
      console.error('Error loading campaign stats:', error);
    }
  };

  const handleCreateCampaign = () => {
    setDialogType('create');
    setDialogOpen(true);
  };

  const handleEditCampaign = (campaign) => {
    setSelectedCampaign(campaign);
    setDialogType('edit');
    setDialogOpen(true);
  };

  const handleViewCampaign = (campaign) => {
    setSelectedCampaign(campaign);
    setTab(1); // Switch to detail tab
  };

  const handleDeleteCampaign = async (campaignId) => {
    if (!window.confirm('Are you sure you want to delete this campaign?')) return;

    try {
      const response = await fetch(`/api/v1/campaigns/${campaignId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { Authorization: `Bearer ${localStorage.getItem('tkn')}` },
      });

      if (response.ok) {
        setSnackbar({ open: true, message: 'Campaign deleted successfully', severity: 'success' });
        loadCampaigns();
        loadStats();
      } else {
        setSnackbar({ open: true, message: 'Failed to delete campaign', severity: 'error' });
      }
    } catch (error) {
      console.error('Error deleting campaign:', error);
      setSnackbar({ open: true, message: 'Failed to delete campaign', severity: 'error' });
    }
  };

  const handleDuplicateCampaign = async (campaignId) => {
    try {
      const response = await fetch(`/api/v1/campaigns/${campaignId}/duplicate`, {
        method: 'POST',
        credentials: 'include',
        headers: { Authorization: `Bearer ${localStorage.getItem('tkn')}` },
      });

      if (response.ok) {
        setSnackbar({ open: true, message: 'Campaign duplicated successfully', severity: 'success' });
        loadCampaigns();
        loadStats();
      } else {
        setSnackbar({ open: true, message: 'Failed to duplicate campaign', severity: 'error' });
      }
    } catch (error) {
      console.error('Error duplicating campaign:', error);
      setSnackbar({ open: true, message: 'Failed to duplicate campaign', severity: 'error' });
    }
  };

  const handleSaveCampaign = async (campaignData) => {
    try {
      const url = dialogType === 'create' ? '/api/v1/campaigns' : `/api/v1/campaigns/${selectedCampaign._id}`;
      const method = dialogType === 'create' ? 'POST' : 'PUT';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('tkn')}`,
        },
        credentials: 'include',
        body: JSON.stringify(campaignData),
      });

      if (response.ok) {
        const data = await response.json();
        setSnackbar({
          open: true,
          message: `Campaign ${dialogType === 'create' ? 'created' : 'updated'} successfully`,
          severity: 'success',
        });
        setDialogOpen(false);
        loadCampaigns();
        loadStats();

        if (dialogType === 'create') {
          setSelectedCampaign(data.campaign);
          setTab(1); // Switch to detail tab
        }
      } else {
        const errorData = await response.json();
        setSnackbar({ open: true, message: errorData.message || 'Failed to save campaign', severity: 'error' });
      }
    } catch (error) {
      console.error('Error saving campaign:', error);
      setSnackbar({ open: true, message: 'Failed to save campaign', severity: 'error' });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'draft': return 'default';
      case 'scheduled': return 'info';
      case 'paused': return 'warning';
      case 'completed': return 'success';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'email': return <EmailIcon />;
      case 'sms': return <SmsIcon />;
      case 'social_media': return <SocialIcon />;
      case 'direct_mail': return <DirectMailIcon />;
      case 'multi_channel': return <CampaignIcon />;
      default: return <CampaignIcon />;
    }
  };

  return (
    <Box sx={{
      p: 3, height: '100%', bgcolor: 'background.default', overflow: 'auto',
    }}
    >
      <Typography variant="h4" fontWeight={700} gutterBottom>
        Campaigns
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Manage your marketing campaigns, track performance, and engage with your audience.
      </Typography>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  <CampaignIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" fontWeight={700} color="primary.main">
                    {stats.totalCampaigns}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Campaigns
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: alpha(theme.palette.success.main, 0.1) }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{ bgcolor: 'success.main' }}>
                  <TrendingIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" fontWeight={700} color="success.main">
                    {stats.averageOpenRate.toFixed(1)}
                    %
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Avg Open Rate
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: alpha(theme.palette.info.main, 0.1) }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{ bgcolor: 'info.main' }}>
                  <PeopleIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" fontWeight={700} color="info.main">
                    {stats.totalSent.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Sent
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: alpha(theme.palette.warning.main, 0.1) }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{ bgcolor: 'warning.main' }}>
                  <AnalyticsIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" fontWeight={700} color="warning.main">
                    {stats.averageClickRate.toFixed(1)}
                    %
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Avg Click Rate
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Main Content */}
      <Paper elevation={2} sx={{ height: 'calc(100vh - 300px)', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 3, py: 2 }}>
            <Tabs value={tab} onChange={(_, v) => setTab(v)}>
              <Tab label="Campaigns" />
              <Tab label="Details" disabled={!selectedCampaign} />
              <Tab label="Analytics" disabled={!selectedCampaign} />
              <Tab label="Templates" />
            </Tabs>
            <Tooltip title="Create New Campaign">
              <IconButton
                color="primary"
                onClick={handleCreateCampaign}
                sx={{
                  bgcolor: 'primary.main',
                  color: 'white',
                  '&:hover': { bgcolor: 'primary.dark' },
                }}
              >
                <AddIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>

        <Box sx={{ flex: 1, overflow: 'hidden' }}>
          {tab === 0 && (
            <CampaignList
              campaigns={campaigns}
              loading={loading}
              onSelect={handleViewCampaign}
              onEdit={handleEditCampaign}
              onDelete={handleDeleteCampaign}
              onDuplicate={handleDuplicateCampaign}
            />
          )}

          {tab === 1 && selectedCampaign && (
            <CampaignDetail
              campaign={selectedCampaign}
              onEdit={handleEditCampaign}
              onDelete={handleDeleteCampaign}
              onDuplicate={handleDuplicateCampaign}
            />
          )}

          {tab === 2 && selectedCampaign && (
            <CampaignAnalytics campaign={selectedCampaign} />
          )}

          {tab === 3 && (
            <CampaignTemplates onSelectTemplate={(template) => {
              setDialogType('create');
              setDialogOpen(true);
              // TODO: Pre-fill form with template
            }}
            />
          )}
        </Box>
      </Paper>

      {/* Campaign Form Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          py: 2,
          px: 3,
        }}
        >
          <CampaignIcon sx={{ fontSize: 32 }} />
          <Typography variant="h6" sx={{ flex: 1, fontWeight: 700 }}>
            {dialogType === 'create' ? 'Create New Campaign' : 'Edit Campaign'}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 2, sm: 4 } }}>
          <CampaignForm
            campaign={selectedCampaign}
            onSave={handleSaveCampaign}
            onCancel={() => setDialogOpen(false)}
            mode={dialogType}
          />
        </DialogContent>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default CampaignsPage;
